import * as PIXI from 'pixi.js';
import type { MapEnvironment } from '@machina/core';

/**
 * Environment color grading.
 *
 * Per design review: "tokens still read as pasted because their color
 * temperature doesn't match the light they're supposedly standing in."
 * Applies a subtle tint matching the map environment so party sprites feel
 * part of the scene instead of composited on top.
 *
 * Applied at the token-layer container level (single filter for all tokens)
 * so per-token work stays minimal.
 */

const ENV_TINTS: Partial<Record<MapEnvironment, number>> = {
  // Warm / fire / forge
  cave_lava: 0xff9955,
  dungeon_temple: 0xffcc99,
  dungeon_mine: 0xccaa88,

  // Cool / cold / crypt
  cave_ice: 0xaaccff,
  snow: 0xccddff,
  dungeon_crypt: 0x8899bb,
  dungeon_prison: 0x9999aa,

  // Sewers / damp
  dungeon_sewer: 0x7f8a66,

  // Nature
  forest_clearing: 0xccddcc,
  forest_dense: 0xaaccaa,
  swamp: 0x99aa88,
  grassland: 0xccddaa,
  jungle_river: 0xaac99a,

  // Arid
  desert: 0xffddaa,
  beach: 0xffeec5,

  // Default stone
  dungeon_stone: 0xccbbaa,

  // Urban
  tavern_interior: 0xffbb77,
  castle_interior: 0xccbb99,
  street_medieval: 0xbbaa99,
  marketplace: 0xccbb99,
  ship_deck: 0xaaccdd,
};

/** Intensity of the grade — high enough to feel unified, low enough to
 *  preserve sprite readability. Design peer suggested ~0.15–0.25. */
const GRADE_ALPHA = 0.20;

/**
 * Build a ColorMatrixFilter that tints sprites toward the environment's
 * ambient color. Returns null for environments with no preset (no grade
 * is applied — tokens render with their original colors).
 */
export function createEnvironmentGrade(
  env?: MapEnvironment | null,
): PIXI.ColorMatrixFilter | null {
  if (!env) return null;
  const tint = ENV_TINTS[env];
  if (tint === undefined) return null;
  const filter = new PIXI.ColorMatrixFilter();
  filter.tint(tint, true); // multiply mode — preserves dark values
  filter.alpha = GRADE_ALPHA;
  return filter;
}
